import { MessageRepository } from "@amityco/ts-sdk";
import { useEffect, useRef, useState } from "react";

import { Button } from "@rio-cloud/rio-uikit";
import { formatNamesGroup } from "@utils";
import { createTextMessage, IChannel, markAllMessageAsRead } from "src/services/amity";

import NonePng from './imgs/profile.png';

interface IChatProps {
    handleBackToList: () => void;
    channel?: IChannel | null;
    userId: string;
}

interface IMessage {
    id: string;
    text: string;
    sender: string;
    createdAt: Date;
}

const Chat = ({ handleBackToList, channel, userId }: IChatProps) => {
    const [newMessage, setNewMessage] = useState('');
    const [chatMessages, setChatMessages] = useState<IMessage[]>([]);
    const messagesRef = useRef<HTMLDivElement>(null);
    const [showAll, setShowAll] = useState(false);

    const handleSendMessage = () => {
        if (newMessage.trim() !== '') {
            createTextMessage(newMessage, channel?.id ?? '');
            setNewMessage('');
        }
    };

    const subscribeToChannelMessages = (channelId: string) => {
        const unsub = MessageRepository.getMessages(
            { subChannelId: channelId, limit: 100 },
            ({ data: messages, error }) => {
                if (error) return;

                if (messages) {
                    setChatMessages(
                        messages.map((message) => ({
                            id: message.messageId,
                            text: (message.data as Amity.ContentDataText).text,
                            sender: message.creatorId,
                            createdAt: new Date(message.createdAt),
                        }))
                    );
                }
            },
        );

        return unsub;
    }

    useEffect(() => {
        if (channel) {
            subscribeToChannelMessages(channel.id);
            channel.hasUnreadMessage = false;
            markAllMessageAsRead(channel.id);

            setTimeout(() => {
                if (messagesRef.current) {
                    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
                }
            }, 100);
        }
    }, [channel]);

    useEffect(() => {
        if (messagesRef.current && chatMessages.length > 0) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
        }
    }, [chatMessages]);

    return (
        <div className="chat-container display-flex flex-column" style={{ height: '100%', overflow: 'hidden' }}>

            <div className="chat-header-fixed">

                <div className="bg-black display-flex align-items-center justify-content-center padding-15">
                    <Button
                        iconOnly
                        iconName='rioglyph rioglyph-arrow-left'
                        className="bg-none border-none padding-0 margin-0"
                        bsSize="lg"
                        bsStyle="default"
                        onClick={handleBackToList}
                    />
                    <div className="flex-1-0 text-center">
                        <span className="text-size-h3 text-uppercase text-bold">Mensagens</span>
                    </div>
                </div>

                <div className="display-flex align-items-center padding-15 gap-15 bg-white">
                    {channel?.members?.length && channel?.members?.length > 1 ? (
                        <span
                            className="rioglyph rioglyph rioglyph-user-group text-color-primary text-size-h3 bg-highlight-lighter rounded-circle padding-10"
                        />
                    ) : (
                        <img
                            src={channel?.profileImage ?? NonePng}
                            alt="Foto de Usuário"
                            className="width-40 aspect-ratio-1 rounded-circle object-cover"
                        />
                    )}
                    <div className="">

                        {showAll ? (
                            <span className="text-size-18 text-color-black font-medium">
                                {channel?.displayName?.split('__')[0]}
                                <span className="text-size-14 cursor-pointer text-color-primary" onClick={() => setShowAll(false)}> mostrar menos</span>
                            </span>
                        ) : (
                            <span className="text-size-18 text-color-black font-medium">
                                {formatNamesGroup(channel?.displayName?.split('__')[0] as string)}
                                {channel?.displayName && channel?.displayName?.split('__')[0].split(',').length > 4 ? <span className="text-size-12 cursor-pointer text-color-primary" onClick={() => setShowAll(true)}> mostrar mais</span> : null}
                            </span>
                        )}
                    </div>

                </div>
            </div>

            <div
                className="chat-messages-area padding-15 display-flex flex-column"
                ref={messagesRef}
                style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
                {
                    [...chatMessages].reverse().map((message, index) => {
                        return (
                            <>
                                <p
                                    key={message.id}
                                    className={`width-250 text-color-white padding-top-15 padding-left-10 padding-right-10 padding-bottom-4 chat-message-bubble ${message.sender === userId ?
                                        'bg-darker sent'
                                        : 'bg-highlight-darker align-self-end received'}`
                                    }

                                >
                                    <span className={`message-text`}>
                                        {message.text}
                                    </span>
                                    <br />
                                    <div className="display-flex justify-self-end">
                                        <span className="text-align-right text-color-gray text-size-10">
                                            {message.createdAt.toLocaleTimeString('pt-Br', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                </p>
                                {(index === 0 || message.createdAt.toDateString() !== [...chatMessages].reverse()[index - 1]?.createdAt.toDateString()) && (
                                    <div className="text-center text-size-12 text-color-gray my-2 mt-6">
                                        {message.createdAt.toLocaleDateString('pt-Br', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </div>
                                )}
                            </>
                        )
                    })
                }
            </div>

            <div className="chat-input-fixed display-flex align-items-center gap-5 padding-15">
                <input
                    type="text"
                    className="flex-grow form-control"
                    placeholder="Mensagem"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                />

                <Button
                    iconOnly
                    iconName='rioglyph rioglyph-send'
                    className="bg-primary border-none"
                    bsSize="md"
                    bsStyle="primary"
                    onClick={handleSendMessage}
                    disabled={newMessage.trim() === ''}
                />
            </div>

        </div>
    )
}

export default Chat;